import { Moment } from "moment";
import WarningIcons from "PFApp/booking/components/warning_icons";
import { useMisalignments } from "PFApp/booking/parts/hooks";
import { CalendarMode, Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { ProjectItem } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { ProfileWarningsIndex } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Availability, AvailabilityRule } from "PFTypes/booking";
import React, { useMemo } from "react";

import { getOriginalResource } from "../../helpers";
import { useBookingRowsContext } from "../context/booking_rows_context";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";

interface WarningIconsPanelProps {
  row: Row;
  isRowExpanded: boolean;
  scrollToDate: (date?: string | null) => void;
  thresholds?: {
    overbooking: {
      color: string;
      min: number;
      max: number;
      utilization: number;
    }[];
  };
}

const isAvailabilityRule = (availability: Availability): availability is AvailabilityRule =>
  "ranges" in availability;

const getRoleStartDate = (utc: (date: string) => Moment, role?: ProjectItem["activity"]) => {
  if (!role || !role.availability) {
    return null;
  }

  if (isAvailabilityRule(role.availability)) {
    const ranges = role.availability?.ranges;
    if (!ranges || !ranges.length) {
      return null;
    }
    const sorted = ranges.sort(({ start: s1 }, { start: s2 }) => utc(s1 as string).diff(utc(s2 as string)));
    return sorted[0].start;
  }

  return null;
};

const getOverbookings = (row: Row, calendarMode: CalendarMode, warningsByProfile: ProfileWarningsIndex) =>
  (row.type === RowType.Profile &&
    calendarMode === CalendarMode.Workforce &&
    warningsByProfile[getOriginalResource(row, calendarMode)?.id!]?.overbookings) ||
  [];

const WarningIconsPanel = ({ row, isRowExpanded, scrollToDate }: WarningIconsPanelProps) => {
  const { calendarMode } = useBookingsOverviewContext();
  const { rowsByCalendarMode, warningsByProfile } = useBookingRowsContext();

  const { rowMisalignments } = useMisalignments({ row, mode: calendarMode });
  const { utc } = useDateFormatter();
  const { type, parents } = row;
  const rows = rowsByCalendarMode[calendarMode];

  const overbookings = getOverbookings(row, calendarMode, warningsByProfile);

  const parentRoleRow: Row | null = useMemo(
    () =>
      calendarMode === CalendarMode.Projects
        ? rows.find(({ key, type }) => type === RowType.Role && parents.includes(key)) ?? null
        : null,
    [rows, parents, calendarMode]
  );

  const roleStartDate = getRoleStartDate(utc, (parentRoleRow?.original as ProjectItem)?.activity);

  return (
    <WarningIcons
      overbookings={overbookings}
      misalignments={rowMisalignments}
      type={type}
      iconNotHighlighted={isRowExpanded}
      scrollToDate={() => scrollToDate(roleStartDate)}
      roleName={(parentRoleRow?.original as ProjectItem)?.activity.name}
    />
  );
};

export default WarningIconsPanel;
